import React, { useContext } from 'react'

import Multistep from 'components/multistep/Multistep'
import { LifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import { ApplicationDispatchContext } from 'contexts/ApplicationContext'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { IMultistepComponent } from 'interfaces/i-multistep'
import { CheckIsLifeCycleStatus } from 'utils/loanHelpers'
import ThankYou from 'components/application-blocks/common-blocks/ThankYou/ThankYou'
import LoanOfferLt from 'components/application-blocks/application-offer-blocks/LoanOffer/LoanOfferLt'
import { SignConsentLt } from 'components/application-blocks/application-offer-blocks/SignConsent/SignConsentLt'
import { FillPersonalDataLt } from 'components/application-blocks/application-offer-blocks/FillPersonalData/FillPersonalDataLt'
import { SignPnLt } from 'components/application-blocks/application-offer-blocks/SignPn/SignPnLt'
import LoanOfferInvolveSpouseLt from 'components/application-blocks/application-offer-blocks/LoanOffer/LoanOfferInvolveSpouseLt'
import Investigation from 'components/application-blocks/common-blocks/Investigation/Investigation'
import SpouseFillPersonalDataLt from 'components/application-blocks/application-offer-blocks/FillPersonalData/SpouseFillPersonalDataLt'
import { UrlStatus } from 'constants/enums/signingUrlTypes'
import { ProductType } from 'constants/enums/productTypes'

export const LoanApplicationStepsLt = () => {
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext
	)

	const isSpouseAdded = () : boolean => {
		return loanApplication.applicationData?.signingUrls?.spouseConsent?.url ? true : false
	}

	const isSpouseConsentSigned = () : boolean => {
		return loanApplication.applicationData?.signingUrls?.spouseConsent?.status === UrlStatus.Signed
	}

	const isFinancingMayBePossible = () : boolean => {
		return (loanApplication.applicationData?.offer?.hasSpouseBetterOffer ?? false) && loanApplication.applicationData?.lifeCycleStatus === LifeCycleStatus.CreditDecisionInvestigation
	}
	
	const loanApplicationSteps: IMultistepComponent[] = [
		{
			id: 1,
			header: 'SignMainApplicantConsent',
			body: <SignConsentLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionAwaitConsents
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) || (loanApplication.applicationData?.lifeCycleStatus === LifeCycleStatus.CreditDecisionInvestigation 
					&& loanApplication.applicationData?.signingUrls?.applicantConsent && loanApplication.applicationData?.signingUrls?.applicantConsent?.status !== UrlStatus.Signed),
			stepCompleted: false,
		},
		{
			id: 2,
			header: 'LoanOfferHeader',
			body: <LoanOfferLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) && !isFinancingMayBePossible(),
			stepCompleted: false,
		},
		{
			id: 3,
			header: 'InvolveSpouse',
			body: <LoanOfferInvolveSpouseLt />,
			show: isFinancingMayBePossible() || ((loanApplication?.applicationData?.offer?.hasSpouseBetterOffer ?? false)
			 	&& !isSpouseAdded() && CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents
				],
				loanApplication.applicationData?.lifeCycleStatus!
			)),
			stepCompleted: false,
		},
		{
			id: 4,
			header: 'FillPersonalData',
			body: <FillPersonalDataLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
		{
			id: 5,
			header: 'SignPn',
			body: <SignPnLt />,
			show: (CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.ReceiveSigning,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) && loanApplication.applicationData?.product !== ProductType.LtuRefin)
			|| CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.ReceiveSigning,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
		{
			id: 6,
			header: 'ThankYou',
			body: <ThankYou />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.ReceiveSigning,
					LifeCycleStatus.DocumentsUpload,
					LifeCycleStatus.InternalCheck,
					LifeCycleStatus.SendToBo,
					LifeCycleStatus.CreditDecisionAwaitSpouseConsents,
					LifeCycleStatus.CollectRefinData,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
		{
			id: 7,
			header: 'InvestigationHeader',
			body: <Investigation />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionInvestigation,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
	]

	const loanApplicationConsentSteps: IMultistepComponent[] = [
		{
			id: 1,
			header: 'SignMainApplicantConsent',
			body: <SignConsentLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionAwaitConsents
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) || (loanApplication.applicationData?.lifeCycleStatus === LifeCycleStatus.CreditDecisionInvestigation 
					&& loanApplication.applicationData?.signingUrls?.applicantConsent && loanApplication.applicationData?.signingUrls?.applicantConsent?.status !== UrlStatus.Signed),
			stepCompleted: false,
		},
		{
			id: 6,
			header: 'ThankYou',
			body: <ThankYou />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.CreditDecisionError,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.ReceiveSigning,
					LifeCycleStatus.DocumentsUpload,
					LifeCycleStatus.InternalCheck,
					LifeCycleStatus.SendToBo,
					LifeCycleStatus.CreditDecisionAwaitSpouseConsents,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
		{
			id: 7,
			header: 'InvestigationHeader',
			body: <Investigation />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionInvestigation,
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
	]

	const loanApplicationSpouseSteps: IMultistepComponent[] = [
		{
			id: 1,
			header: 'FillPersonalData',
			body: <SpouseFillPersonalDataLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionAwaitSpouseConsents,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.CreditDecisionInvestigation
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) && !isSpouseConsentSigned(),
			stepCompleted: false,
		},
		{
			id: 2,
			header: 'SignSpouseConsent',
			body: <SignConsentLt />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionAwaitSpouseConsents,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.CreditDecisionInvestigation
				],
				loanApplication.applicationData?.lifeCycleStatus!
			) && !isSpouseConsentSigned(),
			stepCompleted: false,
		},
		{
			id: 3,
			header: 'ThankYou',
			body: <ThankYou />,
			show: CheckIsLifeCycleStatus(
				[
					LifeCycleStatus.CreditDecisionAwaitSpouseConsents,
					LifeCycleStatus.CreditDecisionAwaitConsents,
					LifeCycleStatus.MultiStepUpdateLoanData,
					LifeCycleStatus.MultiStepUpdatePersonalData,
					LifeCycleStatus.ReceiveSigning,
					LifeCycleStatus.DocumentsUpload,
					LifeCycleStatus.InternalCheck,
					LifeCycleStatus.SendToBo,
					LifeCycleStatus.CreditDecisionInvestigation
				],
				loanApplication.applicationData?.lifeCycleStatus!
			),
			stepCompleted: false,
		},
	]

	const getSteps = () => {
		return loanApplication?.personalData?.isSpouse ? loanApplicationSpouseSteps : 
				(isSpouseAdded() && !isSpouseConsentSigned()) ? loanApplicationConsentSteps : loanApplicationSteps
	}

	return (
		<Multistep
			multisteps={getSteps()}
			resetInitialState={false}
		/>
	)
}
